import { Container } from "pixi.js";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { BigWinView } from "../views/big-win/BigWinView";
import { FeatureFinishedView } from "../views/feature-finished/FeatureFinishedView";
import { FeatureTriggerView } from "../views/feature-trigger/FeatureTriggerView";
import { GameTransitionView } from "../views/game-transition/GameTransitionView";
import { FEATURE_FINISHED_TUNING } from "../tuning/featureFinishedTuning";
import { FEATURE_TRIGGER_TUNING } from "../tuning/featureTriggerTuning";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";
import type { LocalizationService } from "@slot/game-shell";

/**
 * `ModalLayer/GameModalContainer` — the round's modal overlays in scene order: feature trigger, big win, feature
 * finished, game transition (the Cocos gamble panel is not ported: the backend runs no gamble). Every panel is UI
 * chrome: its motion rides the chrome task runner, its Spines are ticked by the shared `SpineDriver`.
 */
export class GameModals extends Container {
  public readonly featureTriggerView: FeatureTriggerView;
  public readonly bigWinView: BigWinView;
  public readonly featureFinishedView: FeatureFinishedView;
  public readonly gameTransitionView: GameTransitionView;

  public constructor(input: {
    assets: GameVisualAssets;
    localization: LocalizationService;
    /** UI-chrome runner (the panels' motion). */
    runner: PresentationTaskRunner;
    viewport: ViewportStore;
    spineDriver: SpineDriver;
  }) {
    super({ label: "GameModalContainer" });
    const { assets, localization, runner, viewport } = input;
    this.featureTriggerView = new FeatureTriggerView({
      backgroundSkeleton: assets.skeletons.background,
      spineDriver: input.spineDriver,
      viewport,
      localization,
      runner,
      motion: FEATURE_TRIGGER_TUNING.anim,
    });
    this.bigWinView = new BigWinView({
      font: assets.fonts.grave,
      titleSheet: assets.sheets.bigWinTitles,
      splatter: assets.textures.bloodSplatter,
      glow: assets.textures.glow,
      viewport,
      localization,
      runner,
    });
    this.featureFinishedView = new FeatureFinishedView({ cover: assets.textures.featureFinishedCover, viewport, localization, runner, motion: FEATURE_FINISHED_TUNING.anim });
    this.gameTransitionView = new GameTransitionView();
    this.addChild(this.featureTriggerView, this.bigWinView, this.featureFinishedView, this.gameTransitionView);
  }

  public dispose(): void {
    this.featureTriggerView.dispose();
    this.bigWinView.dispose();
    this.featureFinishedView.dispose();
    this.destroy({ children: true });
  }
}
